<template lang="pug">
    Tabs.campaign-list
        el-card.contains-table(shadow="always")
            .error(v-if="error")
                template(v-if="error.code")
                    Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
                template(v-else)
                    Error(:message="$t('Something went wrong!')" :code="501")
            .campaigns-table(v-else)
                //- :row-link-config="campaignsPaginated.link"
                paginated-items(
                    scope="ex"
                    type="marketing_campaign"
                    :loading="loading"
                    :items="campaignsPaginated.items"
                    :filters="filters"
                    :show-filters="showFilters"
                    :variables="campaignsPaginated.variables"
                    :all-columns="campaignsPaginated.allColumns"
                    :active-columns="campaignsPaginated.activeColumns"
                    :page-info="campaignsPaginated.pageInfo"
                    :sort="sort"
                    @pagination-size-change="handleSizeChange"
                    @pagination-current-change="handleCurrentChange"
                    @show-table-columns="handleColsShow"
                    @change-filter="handleFilterInputChange"
                    @change-sort="handleSortInputChange"
                    @click="handleClick"
                )
                    template(slot="top")
                        el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                            .left
                                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="addCampaign")
                                    | {{ $t('New Campaign') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}
</template>

<i18n>
{
    "de": {
        "New Campaign": "Neue Kampagne",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "Campaign list could not be fetched.": "Kampagnenliste konnte nicht abgerufen werden."
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useSetupStore, useMarketingCampaignStore } from '@/stores'
import { adminClient as apolloClient } from '@/store/apollo'

import router from '@/router'
import pinia from '@/pinia';

import PaginatedItems from '@/components/PaginatedItems'
import Tabs from '@/views/cms/Marketing/Partials/Tabs'
import Error from '@/components/Error'

export default {
    name: 'Campaigns',
    data() {
        return {
            // loading: true,
            error: false,
            disable: {
                showFilters: false,
            }
        }
    },
    components: {
        PaginatedItems,
        Error,
        Tabs
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true)
            if (reload) this.initialize()
        },
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.campaignsPaginated, 'showFilters') || false
        },
        hasFilters() {
            return ! _.isEmpty(this.filters)
        },
        filters() {
            let filters = _.cloneDeep(this.campaignsPaginated.filters)
            delete filters.cms
            return filters
        },
        campaignStore() {
            return useMarketingCampaignStore()
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions
        },
        debounce() {
            return this.setupStore.debounce
        },
        loading() {
            return this.campaignStore.loading
        },
        campaignsPaginated() {
            return this.campaignStore.campaignsPaginated
        },
        sort() {
            var variables = this.campaignsPaginated.variables;
            var defaultSort = this.campaignsPaginated.defaultSort || { field: 'created_at', direction: 'desc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;
            return { field: order, direction: direction };
        }
    },
    methods: {
        initialize() {
            if (! this.enabledExtensions.includes('marketing')) {
                this.updateLoading(true)
                // this.$router.push({ name: 'marketing' })

                return
            }

            this.updateLoading(true)
            this.refetch()
        },
        getCurrency(item) {
            return _.get(item, 'currency')
        },
        addCampaign() {
            // TODO
            // open stepper modal

            const params = { id: 'new' }
            router.push({ name: 'campaign-main', params })
        },
        handleClick(item) {
            const params = { id: item.id }
            router.push({ name: 'campaign-main', params })
        },
        handleSizeChange(itemsPerPage) {
            this.updateLoading(true)
            this.defineItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange (currentPage) {
            this.updateLoading(true)
            this.defineCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns)
            // apolloClient.cache.reset()
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineFilterValue({ key, value })
            if (debounce) this.debouncedRefetch()
            else this.refetch()
        },
        handleSortInputChange(key, value) {
            this.campaignStore.defineSorting({ key, value })
            this.refetch()
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters

            let value = false
            if (! visible) value = true

            this.disable.showFilters = true
            this.defineFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'ex', key: 'marketing_campaign', value })
                .finally(() => this.disable.showFilters = false)
        },
        refetch() {

            // always add the cms id to filters
            const cmsID = this.activeCms.id
            const filter = {
                key: 'cms',
                match: cmsID,
            }
            this.defineFilterValue({ key: 'cms', value: filter })

            this.fetchCampaignsPaginated()
                .then(() => this.updateLoading(false))
                .catch(err => {
                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    let notify = true;
                    switch (code) {
                        case 401:
                            notify = false;
                            this.error = { code, message: 'You are not authorized' };
                            break;
                        case 403:
                            notify = false;
                            this.error = { code, message: 'You are not allowed to be here' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    if (notify) {
                        this.$notify.error({
                            title: this.$t(message),
                            message: this.$t('Campaign list could not be fetched.'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000
                        });
                    }

                    this.updateLoading(false);

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
        },
        debouncedRefetch: _.debounce(function() { // function scope is needed here
            this.updateLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        // Pinia store methods
        fetchCampaignsPaginated() {
            return this.campaignStore.fetchCampaignsPaginated()
        },
        updateLoading(value) {
            this.campaignStore.updateLoading(value)
        },
        defineItemsPerPage(perPage) {
            this.campaignStore.defineItemsPerPage(perPage)
        },
        defineCurrentPage(currentPage) {
            this.campaignStore.defineCurrentPage(currentPage)
        },
        defineActiveColumns(columns) {
            this.campaignStore.defineActiveColumns(columns)
        },
        defineFilterValue(payload) {
            this.campaignStore.defineFilterValue(payload)
        },
        defineFilterVisibility(value) {
            this.campaignStore.defineFilterVisibility(value)
        },
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload)
        },
    },
    mounted() {
        this.initialize()
    },
}
</script>

<style lang="sass" scoped>
.error
    padding: 20px
</style>
